// Port of SuiteModel/MapLayout.swift: where things go on a map and how notes relate, the same on every canvas. A note's
// `at` is the top-left of its text; its frame's size is measured by the canvas (fonts differ by platform), so the
// functions here take frames, never measure.
import { swiftCompare } from '../model/order.ts';
import { MapArrange } from './arrange.ts';
import { MapSketch } from './sketch.ts';
import { mapNeighbours, mapNoteOf, placedStroke, type MapContent, type MapLine, type MapNote, type MapOutline, type MapPoint } from './map.ts';

/** A note's box on the paper, in canvas points. */
export interface MapFrame { x: number; y: number; width: number; height: number }

export type MapFrames = ReadonlyMap<string, MapFrame>;

export type MapDirection = 'up' | 'down' | 'left' | 'right';

const maxX = (f: MapFrame) => f.x + f.width;
const maxY = (f: MapFrame) => f.y + f.height;
const midX = (f: MapFrame) => f.x + f.width / 2;
const midY = (f: MapFrame) => f.y + f.height / 2;

const intersects = (a: MapFrame, b: MapFrame, margin: number) =>
  a.x - margin < maxX(b) && b.x - margin < maxX(a) && a.y - margin < maxY(b) && b.y - margin < maxY(a);

/** (at.y, at.x, id): nearest the top first, then the left. */
const topLeft = (a: MapNote, b: MapNote) => a.at.y - b.at.y || a.at.x - b.at.x || swiftCompare(a.id, b.id);

/** (createdAt, id): the order notes were made. */
const madeOrder = (a: MapNote, b: MapNote) => a.createdAt.getTime() - b.createdAt.getTime() || swiftCompare(a.id, b.id);

function notesByID(content: MapContent): Map<string, MapNote> {
  const byID = new Map<string, MapNote>();
  for (const n of content.notes) if (!byID.has(n.id)) byID.set(n.id, n);
  return byID;
}

export const MapLayout = {
  /** Room between a parent and a child (Tab) and between siblings (Return). */
  childGap: 72,
  siblingGap: 18,
  /** Notes closer than this count as overlapping when a new one is placed. */
  clearance: 10,

  // MARK: Parents and branches

  /** Each note's parent: with a centre, the note one step nearer it along the lines (breadth first, nearest the top
   *  first); without one, the joined note made before it (the one it was made from), earliest first. */
  parents(content: MapContent): Map<string, string> {
    const parents = new Map<string, string>();
    const byID = notesByID(content);
    if (content.centre !== null) {
      const seen = new Set([content.centre]);
      const queue = [content.centre];
      for (let index = 0; index < queue.length; index++) {
        const id = queue[index];
        const next = mapNeighbours(content, id).filter((n) => !seen.has(n)).flatMap((n) => byID.get(n) ?? []).sort(topLeft);
        for (const note of next) {
          seen.add(note.id);
          parents.set(note.id, id);
          queue.push(note.id);
        }
      }
      return parents;
    }
    for (const note of content.notes) {
      const earlier = mapNeighbours(content, note.id).flatMap((n) => byID.get(n) ?? []).filter((n) => madeOrder(n, note) < 0);
      let parent: MapNote | null = null;
      for (const n of earlier) if (!parent || madeOrder(n, parent) < 0) parent = n;
      if (parent) parents.set(note.id, parent.id);
    }
    return parents;
  },

  parent: (id: string, content: MapContent): string | null => MapLayout.parents(content).get(id) ?? null,

  /** A note's children, nearest the top first. */
  children(id: string, content: MapContent): string[] {
    const parents = MapLayout.parents(content);
    return content.notes.filter((n) => parents.get(n.id) === id).sort(topLeft).map((n) => n.id);
  },

  /** A note and everything further from the centre through it (Move Branch). Without a centre, everything made from it. */
  branch(id: string, content: MapContent): Set<string> {
    const children = new Map<string, string[]>();
    for (const [child, parent] of MapLayout.parents(content)) children.set(parent, [...(children.get(parent) ?? []), child]);
    const result = new Set([id]);
    const stack = [id];
    while (stack.length) {
      const next = stack.pop()!;
      for (const child of children.get(next) ?? []) {
        if (result.has(child)) continue;
        result.add(child);
        stack.push(child);
      }
    }
    return result;
  },

  // MARK: Placing new notes

  /** Moves a new note's frame down, a step at a time, until it's clear of every other note. */
  nudged(frame: MapFrame, others: Iterable<MapFrame>): MapFrame {
    const all = [...others];
    const placed = { ...frame };
    for (let i = 0; i < 400; i++) {
      const blocking = all.find((o) => intersects(o, placed, MapLayout.clearance));
      if (!blocking) return placed;
      placed.y = maxY(blocking) + MapLayout.clearance + 1;
    }
    return placed;
  },

  /** The room a note takes on the paper: its frame grown by its outline (the pencil runs outside the words), and when it
   *  heads a stack, its stack's notes' rooms too. */
  occupied(id: string, content: MapContent, frames: MapFrames): MapFrame | null {
    const members = MapArrange.stacks(content).get(id) ?? [];
    const placed = members.length ? MapArrange.stackPlaces(content, frames) : new Map<string, MapPoint>();
    const own = (n: string): MapFrame | null => {
      const known = frames.get(n);
      if (!known) return null;
      const at = placed.get(n);
      const f = at ? { ...known, x: at.x, y: at.y } : known;
      const outline = mapNoteOf(content, n)?.outline ?? 'none';
      const side = outline === 'box' ? 10
        : outline === 'oval' || outline === 'cloud' ? MapSketch.ovalRadii(f.width, f.height, outline === 'cloud' ? 10 : 0).rx - f.width / 2
        : 0;
      const reach = MapArrange.outlineReach(outline);
      return { x: f.x - side, y: f.y - reach, width: f.width + side * 2, height: f.height + reach * 2 };
    };
    let room = own(id);
    if (!room) return null;
    for (const member of members) {
      const m = own(member);
      if (!m) continue;
      const x0 = Math.min(room.x, m.x), y0 = Math.min(room.y, m.y);
      room = { x: x0, y: y0, width: Math.max(maxX(room), maxX(m)) - x0, height: Math.max(maxY(room), maxY(m)) - y0 };
    }
    return room;
  },

  /** Every note's room (`occupied`), for placing a new note clear of them. */
  rooms(content: MapContent, frames: MapFrames): MapFrame[] {
    return content.notes.flatMap((n) => MapLayout.occupied(n.id, content, frames) ?? []);
  },

  /** Where a child of `parent` goes (Tab): to its right, below its last child, clear of everything (outlines and stacks
   *  included). Pass the frames as drawn (`MapArrange.stacked`). */
  childOrigin(parent: string, size: { width: number; height: number }, content: MapContent, frames: MapFrames): MapPoint {
    const parentRoom = MapLayout.occupied(parent, content, frames);
    if (!parentRoom) return { x: 0, y: 0 };
    const kids = MapLayout.children(parent, content);
    const xs = kids.flatMap((id) => frames.get(id)?.x ?? []);
    const bottoms = kids.flatMap((id) => { const r = MapLayout.occupied(id, content, frames); return r ? [maxY(r)] : []; });
    const x = xs.length ? Math.min(...xs) : maxX(parentRoom) + MapLayout.childGap;
    const y = bottoms.length ? Math.max(...bottoms) + MapLayout.siblingGap : frames.get(parent)?.y ?? 0;
    const frame = MapLayout.nudged({ x, y, width: size.width, height: size.height }, MapLayout.rooms(content, frames));
    return { x: frame.x, y: frame.y };
  },

  /** Where a sibling of `note` goes (Return): below it (and its stack), at its left edge, clear of everything. */
  siblingOrigin(note: string, size: { width: number; height: number }, content: MapContent, frames: MapFrames): MapPoint {
    const frame = frames.get(note), room = MapLayout.occupied(note, content, frames);
    if (!frame || !room) return { x: 0, y: 0 };
    const placed = MapLayout.nudged({ x: frame.x, y: maxY(room) + MapLayout.siblingGap, width: size.width, height: size.height }, MapLayout.rooms(content, frames));
    return { x: placed.x, y: placed.y };
  },

  // MARK: Lines

  /** Where a line from a note's middle towards `target` leaves its outline (the pencil oval or box, else the text with
   *  a little room round it), so lines meet notes at their edges. */
  edgePoint(frame: MapFrame, outline: MapOutline, target: MapPoint): MapPoint {
    const cx = midX(frame), cy = midY(frame);
    const dx = target.x - cx, dy = target.y - cy;
    if (dx === 0 && dy === 0) return { x: cx, y: cy };
    if (outline === 'oval' || outline === 'cloud') {
      // Where the pencil runs: a cloud's bumps reach out about half their lift beyond its oval.
      const { rx, ry } = MapSketch.ovalRadii(frame.width, frame.height, outline === 'cloud' ? 10 : 0);
      const t = 1 / Math.sqrt((dx * dx) / (rx * rx) + (dy * dy) / (ry * ry));
      return { x: cx + dx * t, y: cy + dy * t };
    }
    const padX = outline === 'box' ? 10 : 6, padY = outline === 'box' ? 8 : 5;
    const hx = frame.width / 2 + padX, hy = frame.height / 2 + padY;
    const t = Math.min(dx === 0 ? Infinity : hx / Math.abs(dx), dy === 0 ? Infinity : hy / Math.abs(dy));
    return { x: cx + dx * t, y: cy + dy * t };
  },

  /** A line's two ends: from edge to edge of its notes' outlines. */
  lineEnds(line: MapLine, content: MapContent, frames: MapFrames): [MapPoint, MapPoint] | null {
    let a = frames.get(line.from), b = frames.get(line.to);
    const from = mapNoteOf(content, line.from), to = mapNoteOf(content, line.to);
    if (!a || !b || !from || !to) return null;
    let fromOutline = from.outline, toOutline = to.outline;
    // A line to a stack's head meets the whole stack (the column round it and the notes under it), so it never crosses them.
    const stacks = MapArrange.stacks(content);
    const fromMembers = stacks.get(line.from), toMembers = stacks.get(line.to);
    if (fromMembers && !fromMembers.includes(line.to)) {
      a = MapLayout.column(line.from, fromMembers, frames);
      fromOutline = 'none';
    }
    if (toMembers && !toMembers.includes(line.from)) {
      b = MapLayout.column(line.to, toMembers, frames);
      toOutline = 'none';
    }
    return [
      MapLayout.edgePoint(a, fromOutline, { x: midX(b), y: midY(b) }),
      MapLayout.edgePoint(b, toOutline, { x: midX(a), y: midY(a) }),
    ];
  },

  /** The frame round a stack's head and the notes under it. */
  column(head: string, members: string[], frames: MapFrames): MapFrame {
    let f = frames.get(head);
    if (!f) return { x: 0, y: 0, width: 0, height: 0 };
    for (const m of members) {
      const g = frames.get(m);
      if (!g) continue;
      const minX = Math.min(f.x, g.x), minY = Math.min(f.y, g.y);
      f = { x: minX, y: minY, width: Math.max(maxX(f), maxX(g)) - minX, height: Math.max(maxY(f), maxY(g)) - minY };
    }
    return f;
  },

  // MARK: Moving between notes

  /** The note to step to from `id` (⌥ and an arrow): the nearest joined note whose middle lies that way (within 45° of
   *  it), else the nearest note that way. */
  step(id: string, direction: MapDirection, content: MapContent, frames: MapFrames): string | null {
    const start = frames.get(id);
    if (!start) return null;
    const candidate = (other: string): number | null => {
      const f = frames.get(other);
      if (other === id || !f) return null;
      const dx = midX(f) - midX(start), dy = midY(f) - midY(start);
      const [along, across] = direction === 'up' ? [-dy, dx] : direction === 'down' ? [dy, dx] : direction === 'left' ? [-dx, dy] : [dx, dy];
      if (!(along > 0) || !(Math.abs(across) <= along)) return null;
      return dx * dx + dy * dy;
    };
    const nearest = (ids: string[]): string | null => {
      let best: [string, number] | null = null;
      for (const other of ids) {
        const d = candidate(other);
        if (d === null) continue;
        if (!best || d < best[1] || (d === best[1] && swiftCompare(other, best[0]) < 0)) best = [other, d];
      }
      return best?.[0] ?? null;
    };
    return nearest(mapNeighbours(content, id)) ?? nearest(content.notes.map((n) => n.id));
  },

  /** The order a screen reader goes through the notes: the centre first, then along the lines, each note followed by
   *  what's joined to it (nearest the top first) before its next sibling; then each other group from its top-left note. */
  readingOrder(content: MapContent): string[] {
    const byID = notesByID(content);
    const listed = new Set<string>();
    const order: string[] = [];
    const walk = (id: string) => {
      if (listed.has(id)) return;
      listed.add(id);
      order.push(id);
      const next = mapNeighbours(content, id).filter((n) => !listed.has(n)).flatMap((n) => byID.get(n) ?? []).sort(topLeft);
      for (const note of next) if (!listed.has(note.id)) walk(note.id);
    };
    if (content.centre !== null && byID.has(content.centre)) walk(content.centre);
    for (const note of [...content.notes].sort(topLeft)) walk(note.id);
    return order;
  },

  /** The smallest frame round every note, region and stroke (Fit), or null for an empty map. */
  bounds(content: MapContent, frames: MapFrames): MapFrame | null {
    const all: MapFrame[] = [...frames.values()];
    for (const r of content.regions) all.push({ x: r.at.x, y: r.at.y, width: r.width, height: r.height });
    for (const s of content.strokes) {
      const placed = placedStroke(s);
      if (!placed.length) continue;
      const xs = placed.map((p) => p.x), ys = placed.map((p) => p.y);
      const x = Math.min(...xs), y = Math.min(...ys);
      all.push({ x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y });
    }
    if (!all.length) return null;
    let x0 = all[0].x, y0 = all[0].y, x1 = maxX(all[0]), y1 = maxY(all[0]);
    for (const f of all.slice(1)) {
      x0 = Math.min(x0, f.x);
      y0 = Math.min(y0, f.y);
      x1 = Math.max(x1, maxX(f));
      y1 = Math.max(y1, maxY(f));
    }
    return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
  },
};
